import { useCallback, useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  SupplierPeople,
  SupplierPerson,
} from "@shared-core/data/supplier-people-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import { formatDate } from "@shared-core/domain/purchase-ledger";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../primitives/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import { Skeleton } from "../../primitives/skeleton";

/**
 * *Portal access* on the supplier page's Integration tab (supplier interface mock-up 12; spec 06 §9): who at the
 * supplier has a login to the supplier portal, with *Invite* and *Manage* for staff who may change it
 * (`supplier_access:manage`). Staff add people; there is no self-signup. Inviting emails the person the portal's
 * sign-in link, and their first sign-in with that address makes them active.
 */
export function SupplierPortalAccessCard({
  supplierNo,
}: {
  supplierNo: string;
}) {
  const adapter = useDataAdapter();
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "failed"; message: string }
    | { kind: "loaded"; people: SupplierPeople | null }
  >({ kind: "loading" });
  const [dialog, setDialog] = useState<"invite" | "manage" | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    adapter
      .getSupplierPeople(supplierNo)
      .then((people) => {
        if (!cancelled) setState({ kind: "loaded", people });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            kind: "failed",
            message:
              err instanceof Error
                ? err.message
                : "Portal access did not load.",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, supplierNo]);

  useEffect(() => load(), [load]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Portal access</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {state.kind === "loading" && (
          <Skeleton className="h-5 w-40" aria-label="Loading portal access" />
        )}
        {state.kind === "failed" && (
          <p role="alert" className="text-sm text-red-600">
            Could not load portal access: {state.message}
          </p>
        )}
        {state.kind === "loaded" && state.people && (
          <>
            <p className="text-muted-foreground text-sm">
              {peopleLine(state.people.people)}
            </p>
            {state.people.can_manage && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDialog("invite")}
                >
                  Invite
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDialog("manage")}
                >
                  Manage
                </Button>
              </div>
            )}
            <InviteDialog
              people={state.people}
              open={dialog === "invite"}
              onClose={() => setDialog(null)}
              onChanged={load}
            />
            <ManageDialog
              people={state.people}
              open={dialog === "manage"}
              onClose={() => setDialog(null)}
              onChanged={load}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** "J Smith · K Patel (invited)", the people who can sign in now or soon; deactivated people are under Manage. */
function peopleLine(people: SupplierPerson[]): string {
  const live = people.filter((p) => p.state !== "deactivated");
  if (live.length === 0)
    return "Nobody has a login yet. Orders reach them by email with a link.";
  return live
    .map((p) => (p.state === "invited" ? `${p.name} (invited)` : p.name))
    .join(" · ");
}

function stateLabel(person: SupplierPerson): string {
  if (person.state === "invited")
    return `Invited ${formatDate(person.added_at.slice(0, 10))}, not signed in yet`;
  if (person.state === "active" && person.first_signed_in_at) {
    return `Signed in since ${formatDate(person.first_signed_in_at.slice(0, 10))}`;
  }
  if (person.state === "deactivated" && person.deactivated_at) {
    return `Access removed ${formatDate(person.deactivated_at.slice(0, 10))}`;
  }
  return person.state;
}

function InviteDialog({
  people,
  open,
  onClose,
  onChanged,
}: {
  people: SupplierPeople;
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const adapter = useDataAdapter();
  const id = useId();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  function reset() {
    setName("");
    setEmail("");
    setFields({});
    setMessage(null);
  }

  async function submit() {
    setSaving(true);
    setFields({});
    setMessage(null);
    try {
      await adapter.addSupplierPerson(people, {
        name: name.trim(),
        email: email.trim(),
      });
      toast.success(`Invite sent to ${name.trim()}`);
      reset();
      onClose();
      onChanged();
    } catch (err) {
      const fieldErrors = extractFieldErrors(err);
      if (fieldErrors.length > 0) {
        setFields(
          Object.fromEntries(fieldErrors.map((e) => [e.field, e.message])),
        );
      } else {
        setMessage(err instanceof Error ? err.message : "The invite failed.");
        // A failed email still added the person: show them.
        onChanged();
      }
    } finally {
      setSaving(false);
    }
  }

  const errorCount = Object.keys(fields).length;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite someone at {people.supplier_name}</DialogTitle>
          <DialogDescription>
            They get an email with the supplier portal&apos;s sign-in link, and
            sign in with this address.
          </DialogDescription>
        </DialogHeader>
        {(message || errorCount > 0) && (
          <p role="alert" className="text-sm text-red-600">
            {message ??
              `Correct the ${errorCount === 1 ? "field" : `${errorCount} fields`} marked below.`}
          </p>
        )}
        {(["name", "email"] as const).map((field) => (
          <div key={field} className="space-y-1.5">
            <label htmlFor={`${id}-${field}`} className="text-sm font-medium">
              {field === "name" ? "Name" : "Email"}
            </label>
            <Input
              id={`${id}-${field}`}
              type={field === "email" ? "email" : "text"}
              value={field === "name" ? name : email}
              onChange={(e) =>
                (field === "name" ? setName : setEmail)(e.target.value)
              }
              aria-invalid={fields[field] ? true : undefined}
              aria-describedby={
                fields[field] ? `${id}-${field}-error` : undefined
              }
            />
            {fields[field] && (
              <p id={`${id}-${field}-error`} className="text-sm text-red-600">
                {fields[field]}
              </p>
            )}
          </div>
        ))}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || !name.trim() || !email.trim()}
          >
            Send invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManageDialog({
  people,
  open,
  onClose,
  onChanged,
}: {
  people: SupplierPeople;
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const adapter = useDataAdapter();
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function act(person: SupplierPerson, action: "resend" | "remove") {
    setBusy(`${person.id}:${action}`);
    setMessage(null);
    try {
      if (action === "resend") {
        await adapter.resendSupplierInvite(person);
        toast.success(`Invite sent to ${person.name} again`);
      } else {
        await adapter.deactivateSupplierPerson(person);
        toast.success(`${person.name} can no longer sign in`);
        onChanged();
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "That did not work.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Portal access at {people.supplier_name}</DialogTitle>
          <DialogDescription>
            Removing someone signs them out of the supplier portal on their next
            click. To give them access again, invite them again.
          </DialogDescription>
        </DialogHeader>
        {message && (
          <p role="alert" className="text-sm text-red-600">
            {message}
          </p>
        )}
        {people.people.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Nobody has been invited yet.
          </p>
        ) : (
          <ul className="divide-y" aria-label="People with portal access">
            {people.people.map((person) => (
              <li
                key={person.id}
                className="flex flex-wrap items-center gap-2 py-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">
                    {person.name}{" "}
                    {person.state === "deactivated" && (
                      <Badge variant="secondary">Removed</Badge>
                    )}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {person.email} · {stateLabel(person)}
                  </p>
                </div>
                {person.resend_invite_url && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy !== null}
                    onClick={() => void act(person, "resend")}
                  >
                    Resend invite
                  </Button>
                )}
                {person.deactivate_url && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy !== null}
                    onClick={() => void act(person, "remove")}
                    aria-label={`Remove ${person.name}'s access`}
                  >
                    Remove access
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
